import { KeyRound } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import type { MeDTO } from '../../../shared/dto/auth';
import { MIN_PASSWORD_LENGTH, passwordProblem } from '../../../shared/kdf';
import { ApiFailure } from '../../api/client';
import { changePassword } from '../../api/auth';
import { useToast } from '../../components/toast';
import { Button, ErrorBox, Sheet, Switch, TextInput } from '../../components/ui';
import { useT, type T } from '../../i18n';
import { errorText } from './errorText';
import { SettingsSection } from './parts';

function newPasswordError(t: T, current: string, next: string): string | null {
  if (!next) return null;
  const p = passwordProblem(next);
  if (p === 'too_short') return t('settings.password_too_short', { min: MIN_PASSWORD_LENGTH });
  if (p === 'too_common') return t('settings.password_too_common');
  if (current && next === current) return t('settings.password_same');
  return null;
}

function PasswordSheet({ me, open, onClose }: { me: MeDTO; open: boolean; onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentWrong, setCurrentWrong] = useState(false);

  const nextError = newPasswordError(t, current, next);
  const repeatError = repeat && repeat !== next ? t('settings.password_mismatch') : null;
  const canSubmit = !!current && !!next && !!repeat && !nextError && !repeatError && !busy;

  const reset = () => {
    setCurrent('');
    setNext('');
    setRepeat('');
    setShow(false);
    setError(null);
    setCurrentWrong(false);
  };

  const close = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setCurrentWrong(false);
    try {
      await changePassword(me.email, current, next, me.kdfV);
      toast.show(t('settings.password_changed'), { tone: 'ok' });
      reset();
      onClose();
    } catch (err) {
      if (err instanceof ApiFailure && err.code === 'invalid_credentials') setCurrentWrong(true);
      else setError(errorText(t, err));
    } finally {
      setBusy(false);
    }
  };

  const type = show ? 'text' : 'password';
  return (
    <Sheet
      open={open}
      onClose={close}
      title={t('settings.change_password')}
      footer={
        <>
          <Button variant="secondary" block onClick={close} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="kgr-password-form" block busy={busy} disabled={!canSubmit}>
            {busy ? t('settings.password_working') : t('common.save')}
          </Button>
        </>
      }
    >
      <form id="kgr-password-form" onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4 py-2" noValidate>
        {/* Hidden username lets password managers attach the new password to the right account. */}
        <input type="email" name="username" autoComplete="username" value={me.email} readOnly hidden />
        <TextInput
          label={t('settings.current_password')}
          type={type}
          autoComplete="current-password"
          value={current}
          onChange={(e) => {
            setCurrent(e.target.value);
            setCurrentWrong(false);
          }}
          error={currentWrong ? t('settings.password_wrong') : null}
          autoFocus
        />
        <TextInput
          label={t('settings.new_password')}
          type={type}
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          hint={t('settings.password_rules', { min: MIN_PASSWORD_LENGTH })}
          error={nextError}
        />
        <TextInput
          label={t('settings.repeat_password')}
          type={type}
          autoComplete="new-password"
          value={repeat}
          onChange={(e) => setRepeat(e.target.value)}
          error={repeatError}
        />
        <Switch label={t('settings.show_passwords')} checked={show} onChange={setShow} />
        {error ? <ErrorBox>{error}</ErrorBox> : null}
      </form>
    </Sheet>
  );
}

export function PasswordSection({ me }: { me: MeDTO }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <SettingsSection icon={KeyRound} title={t('settings.password')} id="settings-password">
      <div className="flex flex-wrap items-center gap-4 p-5">
        <p className="min-w-0 flex-1 text-[var(--ink-2)]">{t('settings.password_desc')}</p>
        <Button variant="secondary" icon={KeyRound} onClick={() => setOpen(true)}>
          {t('settings.change_password')}
        </Button>
      </div>
      <PasswordSheet me={me} open={open} onClose={() => setOpen(false)} />
    </SettingsSection>
  );
}
